import type { RefObject } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { StyledCheckbox } from '#PRIVATE_StyledCheckbox';
import { CheckIcon } from '#PRIVATE_ThemedIcons/CheckIcon';
import { RadioOffIcon } from '#PRIVATE_ThemedIcons/RadioOffIcon';
import { RadioOnIcon } from '#PRIVATE_ThemedIcons/RadioOnIcon';
import { useCheckboxSetContext } from '#UNSAFE_CheckboxSet';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { useRadioItemContext } from '#UNSAFE_RadioItem/RadioItemContext';
import { radioIconVars } from '#UNSAFE_RadioItem/themes/RadioIconContract.css';
import { RadioIconVariantOptions } from '#UNSAFE_RadioItem/themes/RadioIconStyles.css';
import { radioDefaultColor, radioDisabledColor } from '#UNSAFE_RadioItem/themes/RadioStyles.css';
import { RadioIconRedwoodTheme } from '#UNSAFE_RadioItem/themes/redwood/RadioIconTheme';
import { useRadioSetContext } from '#UNSAFE_RadioSet/RadioSetContext';
import { classNames } from '#utils/UNSAFE_classNames';
import { styles } from './themes/RichSelectionItemStyles.css';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { getRadioSelectionText } from '#UNSAFE_RadioItem/RadioItem';

/**
 * Selective Element Props Type.
 */
type RichSelectionControlProps = {
  ariaLabel?: string; // JET-72374: The ariaLabel for RichSelection options is only used for creating the readonly text.
  value: string | number;
  inputRef?: RefObject<HTMLInputElement>;
  inputReadonlyRef?: RefObject<HTMLInputElement>;
  isFocus?: boolean;
  isLayoutXL?: boolean;
  isDisabled?: boolean;
  isReadonly?: boolean;
  isSelected?: boolean;
  describedBy?: string;
  labelledBy: string;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
};

export const RadioControl = ({
  ariaLabel,
  value,
  inputRef,
  isFocus,
  isDisabled,
  isLayoutXL,
  isReadonly,
  isSelected,
  describedBy,
  labelledBy,
  onBlur,
  onFocus
}: RichSelectionControlProps) => {
  const { name, value: radioGroupValue, onCommit, setFocusIndex } = useRadioSetContext();

  const IconComp = isReadonly
    ? isSelected
      ? CheckIcon
      : RadioOffIcon
    : isSelected
    ? RadioOnIcon
    : RadioOffIcon;

  const spanRef = useRef<HTMLSpanElement>(null);
  const { interactionProps, applyActiveStyle } = useInteractionStyle();

  const onChangeHandler = useCallback(
    (event: Event) => {
      event.stopPropagation();
      onCommit?.({ previousValue: radioGroupValue, value });
    },
    [value, radioGroupValue, onCommit]
  );

  /**
   * When focused the view should have the respective RichSelectionItem.
   */
  useEffect(() => {
    const isActiveElement = document.activeElement === inputRef?.current;
    if (!isActiveElement && isFocus) {
      spanRef?.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [inputRef, isFocus, spanRef]);

  const radioColor = isDisabled ? radioDisabledColor : radioDefaultColor;
  const { classes: radioIconClasses } = useComponentTheme<RadioIconVariantOptions>(
    RadioIconRedwoodTheme,
    {
      disabled: isDisabled ? 'isDisabled' : 'notDisabled',
      selected: isSelected ? 'isSelected' : 'notSelected',
      active: applyActiveStyle ? 'isActive' : 'notActive',
      richSelection: isLayoutXL ? 'isLayoutXL' : 'notLayoutXL'
    }
  );

  const { isTabbable } = useTabbableMode();
  const { index } = useRadioItemContext();
  const tabIndex =
    isTabbable !== false && ((index === 0 && !radioGroupValue) || isSelected) ? 0 : -1;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const readonlyLabel = `${ariaLabel} ${translations.formControl_readOnly()} ${getRadioSelectionText(
    translations,
    !!isSelected
  )}`;

  const hiddenInput = (
    <HiddenAccessible>
      {isReadonly ? (
        <div
          aria-describedby={describedBy}
          data-checked={isSelected}
          ref={inputRef}
          tabIndex={tabIndex}
          onBlur={onBlur}
          onFocus={(event) => {
            setFocusIndex?.(index);
            onFocus?.(event);
          }}>
          {readonlyLabel}
        </div>
      ) : (
        <input
          type="radio"
          name={name}
          value={value}
          {...(!isDisabled && {
            onChange: onChangeHandler
          })}
          checked={isSelected}
          disabled={isDisabled}
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          tabIndex={tabIndex}
          onBlur={onBlur}
          onFocus={onFocus}
          ref={inputRef}
        />
      )}
    </HiddenAccessible>
  );

  // Radio control is visually hidden in readonly mode unless it is selected
  if (!isSelected && isReadonly) {
    return <span class={styles.inputElement}>{hiddenInput}</span>;
  }

  return (
    <span
      ref={spanRef}
      class={classNames([radioColor, isLayoutXL ? styles.radioWrapper : undefined])}>
      {hiddenInput}
      <span aria-hidden="true" class={radioIconClasses} {...interactionProps}>
        <IconComp size={radioIconVars.iconSize} color="currentColor" />
      </span>
    </span>
  );
};

export const CheckboxControl = ({
  ariaLabel,
  value,
  inputRef,
  inputReadonlyRef,
  isSelected,
  isLayoutXL,
  isDisabled,
  isReadonly,
  describedBy,
  labelledBy,
  onBlur,
  onFocus
}: RichSelectionControlProps) => {
  const { name } = useCheckboxSetContext();
  const { isTabbable } = useTabbableMode();
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const checkboxStyles = isLayoutXL ? styles.checkboxWrapper : styles.checkboxAlign;
  // JET-72374: We can't use the label content on its own for readonly because we also need to provide the state of the checkbox.
  const readonlyLabel = `${ariaLabel} ${translations.formControl_readOnly()} ${
    isSelected ? translations.checkbox_selected() : translations.checkbox_unselected()
  }`;

  // Checkbox control is visually hidden in readonly mode unless it is selected
  if (!isSelected && isReadonly) {
    return (
      <span class={styles.inputElement}>
        <HiddenAccessible>
          <div
            onBlur={onBlur}
            onFocus={onFocus}
            ref={inputReadonlyRef}
            tabIndex={isTabbable ? 0 : -1}>
            {readonlyLabel}
          </div>
        </HiddenAccessible>
      </span>
    );
  }

  return (
    <span class={checkboxStyles}>
      <StyledCheckbox
        aria-describedby={describedBy}
        isDisabled={isDisabled}
        isReadonly={isReadonly}
        isChecked={isSelected ? 'checked' : 'unchecked'}
        name={name}
        value={value}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={isReadonly ? inputReadonlyRef : inputRef}
        {...(isReadonly ? { 'aria-label': readonlyLabel } : { 'aria-labelledby': labelledBy })}
      />
    </span>
  );
};
